import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Building2, CalendarClock, CheckCircle2, LockKeyhole, MessagesSquare, Share2, Users } from "lucide-react";

const features = [
  [CalendarClock, "Editorial planning", "Authorized teams plan, review and schedule content for connected business accounts."],
  [Share2, "Platform publishing", "Approved posts can be sent to the selected company-owned social media accounts through official APIs."],
  [BarChart3, "Reporting", "Published posts and permitted performance metrics are synchronized into a tenant-specific reporting view."],
  [MessagesSquare, "Engagement overview", "Where platform permissions allow it, authorized users can review comments and engagement in one workspace."],
];

export default function About({ tenant = "comrocket" }) {
  const isMpai = tenant === "mpai";
  const brandName = isMpai ? "MPAI Social" : "Comrocket Social";
  const iconPath = isMpai ? "/mpai-social-icon.png" : "/comrocket-social-icon.jpg";

  useEffect(() => {
    document.title = `${brandName} | Social Media Management`;
    const icon = document.querySelector("link[rel='icon']");
    const appleIcon = document.querySelector("link[rel='apple-touch-icon']");
    if (icon) icon.href = iconPath;
    if (appleIcon) appleIcon.href = iconPath;
    return () => {
      document.title = "Comrocket Social | Social Media Management";
      if (icon) {
        icon.type = "image/jpeg";
        icon.href = "/comrocket-social-icon.jpg";
      }
      if (appleIcon) appleIcon.href = "/comrocket-social-icon.jpg";
    };
  }, [brandName, iconPath]);

  return (
    <main className="min-h-screen bg-[#f7f8fa] text-slate-950">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between gap-5">
          <div className="flex items-center gap-3">
            <img src={iconPath} alt={`${brandName} icon`} className="h-10 w-10 rounded-lg object-contain" />
            <div>
            <div className="text-lg font-bold tracking-tight">{brandName}</div>
            <div className="text-xs text-slate-500 mt-0.5">Social media operations for authorized business teams</div>
            </div>
          </div>
          <Link to="/login" className="rounded-full bg-black text-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-800">
            Sign in
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24 grid lg:grid-cols-[1.25fr_0.75fr] gap-10 items-center">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 mb-5">Product information</div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-[-0.04em] leading-[1.05] max-w-4xl">
            Plan, publish and review business social media content.
          </h1>
          <p className="text-lg text-slate-600 leading-8 mt-7 max-w-3xl">
            {isMpai ? "MPAI Social is a branded customer workspace within Comrocket Social, a customer-facing multi-tenant web application operated by Comrocket GmbH. The service is offered to independent organizations that use separated workspaces to manage editorial plans, connect company-owned social media accounts, publish approved content and review reporting data." : "Comrocket Social is a customer-facing, multi-tenant web application operated by Comrocket GmbH. Independent businesses and agency customers use their own separated workspace to manage editorial plans, connect company-owned social media accounts, publish approved content and review reporting data."}
          </p>
        </div>
        <aside className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
          <LockKeyhole className="text-slate-700 mb-5" size={28} />
          <h2 className="text-xl font-semibold">Secure customer workspaces</h2>
          <p className="text-sm text-slate-600 leading-6 mt-3">
            Each customer organization receives a separated tenant workspace. Customer administrators invite their own authorized team members and connect only the social accounts they are permitted to manage.
          </p>
          <div className="mt-6 pt-5 border-t border-slate-100 text-sm text-slate-500">
            The platform is offered to external business customers. It is not limited to Comrocket&apos;s internal social accounts.
          </div>
        </aside>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="max-w-3xl mb-10">
          <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 mb-3">Who uses the platform</div>
          <h2 className="text-3xl font-bold tracking-tight">Built for independent customer organizations</h2>
          <p className="text-slate-600 leading-7 mt-4">
            {isMpai ? "MPAI is one external customer organization using a dedicated tenant of Comrocket Social. The underlying service is available to additional independent business customers; each receives a separate workspace. Content, connected accounts, credentials, posts and reporting remain assigned to the respective customer tenant." : "Comrocket Social serves multiple external organizations, including brands and agency customers such as MPAI. Content, connected accounts, credentials, posts and reporting remain assigned to the respective customer tenant."}
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <article className="border border-slate-200 bg-white rounded-xl p-6">
            <Building2 size={24} className="text-slate-600 mb-4" />
            <h3 className="font-semibold text-lg">Separate customer tenants</h3>
            <p className="text-sm text-slate-600 leading-6 mt-2">Every customer works with its own projects, social connections, media assets and publishing queue.</p>
          </article>
          <article className="border border-slate-200 bg-white rounded-xl p-6">
            <Users size={24} className="text-slate-600 mb-4" />
            <h3 className="font-semibold text-lg">Authorized customer teams</h3>
            <p className="text-sm text-slate-600 leading-6 mt-2">Only users invited by the customer organization can access its workspace or publish to its connected accounts.</p>
          </article>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="mailto:info@comrocket.de?subject=Comrocket%20Social%20customer%20access" className="rounded-full bg-black text-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-800">
            Request customer access
          </a>
          <Link to="/login" className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-50">
            Customer sign in
          </Link>
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="max-w-2xl mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 mb-3">Core capabilities</div>
            <h2 className="text-3xl font-bold tracking-tight">A controlled publishing workflow</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {features.map(([Icon, title, text]) => (
              <article key={title} className="border border-slate-200 rounded-xl p-6">
                <Icon size={22} className="text-slate-600 mb-4" />
                <h3 className="font-semibold text-lg">{title}</h3>
                <p className="text-sm text-slate-600 leading-6 mt-2">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 mb-3">TikTok integration</div>
          <h2 className="text-3xl font-bold tracking-tight">How TikTok is used</h2>
          <p className="text-slate-600 leading-7 mt-5">
            An authorized user deliberately starts the TikTok connection from the Social Accounts area and completes TikTok&apos;s OAuth consent flow. Comrocket Social uses only the permissions required for that workflow.
          </p>
        </div>
        <div className="space-y-4">
          {[
            ["user.info.basic", "Identifies the TikTok profile selected by the user and displays its account name in the connection overview."],
            ["video.upload", "Transfers an explicitly selected and approved video to the connected TikTok account for completion in TikTok."],
            ["video.publish", "Publishes an explicitly approved video to the connected TikTok account while respecting the creator settings returned by TikTok."],
          ].map(([scope, text]) => (
            <div key={scope} className="flex gap-4 bg-white border border-slate-200 rounded-xl p-5">
              <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-mono text-sm font-semibold">{scope}</div>
                <p className="text-sm text-slate-600 leading-6 mt-1">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-2xl font-semibold">Data and account control</h2>
            <p className="text-slate-300 leading-7 mt-4">
              OAuth credentials are handled securely. Comrocket Social does not store a user&apos;s TikTok password. Connections can be removed, and publication remains subject to platform permissions and internal approval controls.
            </p>
          </div>
          <div className="md:text-right">
            <div className="font-semibold">Comrocket GmbH</div>
            <div className="text-sm text-slate-400 mt-2">Cologne, Germany · info@comrocket.de</div>
          </div>
        </div>
      </section>

      <footer className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-600">
          <span>© 2026 Comrocket GmbH</span>
          <Link className="underline underline-offset-4" to="/privacy">Privacy Policy</Link>
          <Link className="underline underline-offset-4" to="/terms">Terms of Service</Link>
          <a className="underline underline-offset-4" href="https://comrocket.de/impressum">Legal Notice</a>
          <Link className="underline underline-offset-4" to="/login">Authorized user login</Link>
        </div>
      </footer>
    </main>
  );
}
